import React, { useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const AnimatedBackground3D = () => {
  const { isDark } = useTheme();
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // 3D Box Particle class with enhanced depth
    class Box3D {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.z = Math.random() * 1500 + 500;
        // Size variations: small (15-25), medium (25-40), large (40-60)
        this.baseSize = Math.random() * 45 + 15;
        this.speedX = (Math.random() - 0.5) * 0.8;
        this.speedY = (Math.random() - 0.5) * 0.8;
        this.speedZ = Math.random() * 1.5 + 0.5;
        this.rotationX = Math.random() * 360;
        this.rotationY = Math.random() * 360;
        this.rotationZ = Math.random() * 360;
        this.rotationSpeedX = (Math.random() - 0.5) * 1.5;
        this.rotationSpeedY = (Math.random() - 0.5) * 1.5;
        this.rotationSpeedZ = (Math.random() - 0.5) * 1.5;
        this.opacity = Math.random() * 0.6 + 0.4;
        
        // Purple gradient shades
        const purpleShades = [
          { r: 147, g: 51, b: 234 },   // purple-600
          { r: 168, g: 85, b: 247 },   // purple-500
          { r: 192, g: 132, b: 252 },  // purple-400
          { r: 126, g: 34, b: 206 },   // purple-700
          { r: 109, g: 40, b: 217 },   // purple-800
        ];
        this.color = purpleShades[Math.floor(Math.random() * purpleShades.length)];
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        this.z -= this.speedZ;
        this.rotationX += this.rotationSpeedX;
        this.rotationY += this.rotationSpeedY;
        this.rotationZ += this.rotationSpeedZ;

        // Reset if particle goes out of bounds
        if (this.z <= 0 || this.x < -200 || this.x > canvas.width + 200 || 
            this.y < -200 || this.y > canvas.height + 200) {
          this.reset();
        }
      }

      draw() {
        const scale = 1000 / (this.z + 1000);
        const x = this.x * scale + canvas.width / 2 * (1 - scale);
        const y = this.y * scale + canvas.height / 2 * (1 - scale);
        const size = this.baseSize * scale;

        if (size < 1) return; // Don't draw if too small

        ctx.save();
        ctx.translate(x, y);

        // Calculate 3D rotation
        const radX = (this.rotationX * Math.PI) / 180;
        const radY = (this.rotationY * Math.PI) / 180;
        const radZ = (this.rotationZ * Math.PI) / 180;

        // Draw 3D box with multiple faces
        const faces = this.calculate3DFaces(size, radX, radY, radZ);
        
        // Sort faces by z-depth for proper rendering
        faces.sort((a, b) => a.depth - b.depth);

        // Draw each face
        faces.forEach(face => {
          if (face.depth > 0) { // Only draw visible faces
            ctx.beginPath();
            ctx.moveTo(face.points[0].x, face.points[0].y);
            face.points.forEach(point => {
              ctx.lineTo(point.x, point.y);
            });
            ctx.closePath();

            // Create gradient for 3D effect
            const brightness = Math.max(0.3, face.brightness);
            const alpha = this.opacity * scale * brightness;
            ctx.fillStyle = `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${alpha})`;
            ctx.fill();

            // Add border
            ctx.strokeStyle = `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${alpha * 1.5})`;
            ctx.lineWidth = Math.max(0.5, scale);
            ctx.stroke();
          }
        });

        ctx.restore();
      }

      calculate3DFaces(size, rotX, rotY, rotZ) {
        const s = size / 2;
        
        // Define cube vertices
        const vertices = [
          { x: -s, y: -s, z: -s },
          { x: s, y: -s, z: -s },
          { x: s, y: s, z: -s },
          { x: -s, y: s, z: -s },
          { x: -s, y: -s, z: s },
          { x: s, y: -s, z: s },
          { x: s, y: s, z: s },
          { x: -s, y: s, z: s },
        ];

        // Apply 3D rotations
        const rotated = vertices.map(v => {
          let { x, y, z } = v;
          
          // Rotate around X axis
          let tempY = y * Math.cos(rotX) - z * Math.sin(rotX);
          let tempZ = y * Math.sin(rotX) + z * Math.cos(rotX);
          y = tempY;
          z = tempZ;
          
          // Rotate around Y axis
          let tempX = x * Math.cos(rotY) + z * Math.sin(rotY);
          tempZ = -x * Math.sin(rotY) + z * Math.cos(rotY);
          x = tempX;
          z = tempZ;
          
          // Rotate around Z axis
          tempX = x * Math.cos(rotZ) - y * Math.sin(rotZ);
          tempY = x * Math.sin(rotZ) + y * Math.cos(rotZ);
          x = tempX;
          y = tempY;
          
          return { x, y, z };
        });

        // Define faces (indices into vertices array)
        const faceIndices = [
          [0, 1, 2, 3], // front
          [5, 4, 7, 6], // back
          [4, 5, 1, 0], // top
          [3, 2, 6, 7], // bottom
          [1, 5, 6, 2], // right
          [4, 0, 3, 7], // left
        ];

        return faceIndices.map(indices => {
          const points = indices.map(i => rotated[i]);
          const avgZ = points.reduce((sum, p) => sum + p.z, 0) / points.length;
          
          // Calculate normal for lighting
          const v1 = { 
            x: points[1].x - points[0].x, 
            y: points[1].y - points[0].y, 
            z: points[1].z - points[0].z 
          };
          const v2 = { 
            x: points[2].x - points[0].x, 
            y: points[2].y - points[0].y, 
            z: points[2].z - points[0].z 
          };
          
          // Cross product for normal
          const normal = {
            x: v1.y * v2.z - v1.z * v2.y,
            y: v1.z * v2.x - v1.x * v2.z,
            z: v1.x * v2.y - v1.y * v2.x
          };
          
          // Simple lighting calculation
          const brightness = Math.max(0, normal.z) / 100 + 0.5;
          
          return {
            points: points,
            depth: avgZ,
            brightness: brightness
          };
        });
      }
    }

    // Initialize particles with 3D boxes
    const particleCount = 40;
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Box3D());
    }

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isDark]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full pointer-events-none z-0"
      style={{ opacity: isDark ? 0.5 : 0.3 }}
    />
  );
};

export default AnimatedBackground3D;
